// Updated service-hooks/use-neo4j-graph-service.ts with conditional loading
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { toast } from "sonner";
import { ArchitectureComponent } from "@/types/unified-model";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";

const BASE_URL = `${config.isometricApiUrl}/architectural-graph`;

// Type definitions
export interface PaginatedGraphResponse {
  data: any[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface GraphResponse {
  architectural_specs: Record<string, number>;
}

export interface GraphNode {
  id: string;
  label: string;
  properties: Record<string, any>;
}

export type GraphLabel =
  | "UserExperience"
  | "ApiGateway"
  | "ObservabilityMonitoring"
  | "Agents"
  | "Services"
  | "EventQueue"
  | "DataLake"
  | "Infrastructure";

// Utility function to convert category names to Neo4j labels
export function categoryToLabel(category: string): GraphLabel {
  const mapping: Record<string, GraphLabel> = {
    "user_experience": "UserExperience",
    "api_gateway": "ApiGateway",
    "observability_monitoring": "ObservabilityMonitoring",
    "agents": "Agents",
    "services": "Services",
    "event_queue": "EventQueue",
    "data_lake": "DataLake",
    "infrastructure": "Infrastructure",
  };

  return mapping[category] || "Services";
}

// Utility function to convert Neo4j labels back to category names
export function labelToCategory(label: string): string {
  const mapping: Record<string, string> = {
    "UserExperience": "user_experience",
    "ApiGateway": "api_gateway",
    "ObservabilityMonitoring": "observability_monitoring",
    "Agents": "agents",
    "Services": "services",
    "EventQueue": "event_queue",
    "DataLake": "data_lake",
    "Infrastructure": "infrastructure",
  };

  return mapping[label] || "services";
}

// Transform Neo4j graph node to ArchitectureComponent
export function transformGraphNodeToComponent(node: GraphNode): ArchitectureComponent {
  const { id, label, properties } = node;
  const safeName = typeof properties.name === "string" ? properties.name : "Unnamed Component";
  const safeDescription = typeof properties.description === "string" ? properties.description : "";

  return {
    id,
    type: properties.type || "service",
    shape: properties.shape || "rectangle",
    source: properties.source || "component",
    position: properties.position || "center",
    relativeToId: properties.relativeToId || "",
    attached2DShapes: properties.attached2DShapes || [],
    group: labelToCategory(label),
    name: safeName,
    description: safeDescription,
    scenarios: properties.scenarios || [],
    citationRefs: Array.isArray(properties.citationRefs) ? properties.citationRefs : [],
    metadata: {
      name: safeName,
      blueprint: {
        ...properties,
        name: safeName,
        description: safeDescription,
        group: labelToCategory(label),
      },
    },
  };
}

// UPDATED: Main hook to get the complete graph with conditional enabling
export function useGetGraph(projectUuid: string, enabled: boolean = true) {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["neo4j-graph", projectUuid],
    queryFn: async (): Promise<GraphResponse> => {
      const headers = getAuthHeaders();

      const response = await apiFetch(`${BASE_URL}/${projectUuid}`, {
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
      });


      if (!response.ok) {
        throw new Error(`Failed to fetch architecture graph: ${response.statusText}`);
      }

      const json = await response.json();
      return json; // Return the full response which contains architectural_specs
    },
    enabled: enabled && !!projectUuid, // UPDATED: Support conditional enabling
    staleTime: 30000, // 30 seconds
    retry: 2,
  });
}

export function useNeo4jGraphService(projectUuid: string) {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  // Get nodes by label with pagination
  const useGetNodesByLabel = (label: GraphLabel, page: number = 1, limit: number = 10) => {
    return useQuery({
      queryKey: ["neo4j-nodes", projectUuid, label, page, limit],
      queryFn: async (): Promise<PaginatedGraphResponse> => {
        const headers = getAuthHeaders();

        const response = await apiFetch(
          `${BASE_URL}/${projectUuid}/${label}?page=${page}&limit=${limit}`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...headers,
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch ${label} nodes: ${response.statusText}`);
        }

        const json = await response.json();

        return {
          data: json.data || [],       // Normalize response
          total: json.total || 0,
          page,
          limit,
          hasMore: (json.data?.length || 0) === limit,
        };
      },
      enabled: !!projectUuid && !!label,
      staleTime: 30000,
      retry: 2,
    });
  };

  // Create node mutation
  const useCreateNode = () => {
    return useMutation({
      mutationFn: async ({
        label,
        data
      }: {
        label: string;
        data: Record<string, any>
      }) => {
        const headers = getAuthHeaders();

        const response = await apiFetch(`${BASE_URL}/${projectUuid}/create-node`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
          body: JSON.stringify({ label, data }),
        });

        if (!response.ok) {
          let errorMessage = `Failed to create node: ${response.statusText}`;
          try {
            const errJson = await response.json();
            if (errJson?.message) errorMessage = errJson.message;
          } catch {
            const errorText = await response.text();
            if (errorText) errorMessage = errorText;
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();
        return result;
      },
      onSuccess: () => {
        // Invalidate and refetch graph data
        queryClient.invalidateQueries({ queryKey: ["neo4j-graph", projectUuid] });
        queryClient.invalidateQueries({ queryKey: ["neo4j-nodes", projectUuid] });
        queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
        toast.success("Component created successfully");
      },
      onError: (error) => {
        console.error("Error creating node:", error);
        toast.error("Failed to create component");
      },
    });
  };

  // Update node mutation
  const useUpdateNode = () => {
    return useMutation({
      mutationFn: async ({
        nodeId,
        label,
        data
      }: {
        nodeId: string;
        label: string;
        data: Record<string, any>
      }) => {
        const headers = getAuthHeaders();

        const response = await apiFetch(`${BASE_URL}/${projectUuid}/update-node/${nodeId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
          body: JSON.stringify({ label, data }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Update node error:', errorText);
          throw new Error(`Failed to update node: ${response.statusText} - ${errorText}`);
        }

        const json = await response.json();
        return json.data;
      },
      onSuccess: () => {
        // Invalidate and refetch graph data
        queryClient.invalidateQueries({ queryKey: ["neo4j-graph", projectUuid] });
        queryClient.invalidateQueries({ queryKey: ["neo4j-nodes", projectUuid] });
        queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
        toast.success("Component updated successfully");
      },
      onError: (error) => {
        console.error("Error updating node:", error);
        toast.error("Failed to update component");
      },
    });
  };

  // Delete node mutation
  const useDeleteNode = () => {
    return useMutation({
      mutationFn: async ({
        label,
        nodeId
      }: {
        label: GraphLabel;
        nodeId: string
      }) => {
        const headers = getAuthHeaders();

        const response = await apiFetch(
          `${BASE_URL}/${projectUuid}/${label}/delete-node/${nodeId}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              ...headers,
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Delete node error:', errorText);
          throw new Error(`Failed to delete node: ${response.statusText} - ${errorText}`);
        }

        const json = await response.json();
        return json.data?.deletedCount || 0;
      },
      onSuccess: () => {
        // Invalidate and refetch graph data
        queryClient.invalidateQueries({ queryKey: ["neo4j-graph", projectUuid] });
        queryClient.invalidateQueries({ queryKey: ["neo4j-nodes", projectUuid] });
        queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
        toast.success("Component deleted successfully");
      },
      onError: (error) => {
        console.error("Error deleting node:", error);
        toast.error("Failed to delete component");
      },
    });
  };

  // Upsert (bulk operations) mutation for adding new components
  const useUpsertGraph = () => {
    return useMutation({
      mutationFn: async (graphData: Record<string, any[]>) => {
        const headers = getAuthHeaders();

        const response = await apiFetch(`${BASE_URL}/upsert`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
          body: JSON.stringify({
            project: { uuid: projectUuid },
            ...graphData,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Upsert error:', errorText);
          throw new Error(`Failed to upsert graph: ${response.statusText} - ${errorText}`);
        }

        const json = await response.json();
        return json.data;
      },
      onSuccess: () => {
        // Invalidate and refetch graph data
        queryClient.invalidateQueries({ queryKey: ["neo4j-graph", projectUuid] });
        queryClient.invalidateQueries({ queryKey: ["neo4j-nodes", projectUuid] });
        queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
        toast.success("Graph updated successfully");
      },
      onError: (error) => {
        console.error("Error upserting graph:", error);
        toast.error("Failed to update graph");
      },
    });
  };

  // Return all hook functions so they can be used externally
  return {
    useGetNodesByLabel,
    useCreateNode,
    useUpdateNode,
    useDeleteNode,
    useUpsertGraph,
  };
}